import React, { useState, useEffect } from "react";
import { Printer } from "lucide-react";
import { api } from "../../utils/api";
import PageHead from "../../components/PageHead";

const money = n => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const money2 = n => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtTR = d => {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// 1. BURAYA DIŞARIDAN GELEN 'initialKodu' PARAMETRESİNİ EKLİYORUZ
export default function Balance({ initialKodu }) {
  const [owners, setOwners] = useState([]);
  const [selected, setSelected] = useState(initialKodu || "");
  const [startDate, setStartDate] = useState("");
  const [data, setData] = useState(null);

  const loadStatement = (code, s) => {
    if(!code) return;
    api.get(`/statement/${code}${s ? `?start=${s}` : ""}`).then(x => {
      setData(x.data);
      if (!s) setStartDate(x.data.start);
    }).catch(err => console.error("Hesap dökümü çekilemedi:", err));
  };

  // 2. İLK AÇILIŞ: Malikleri çek ve uygun kişiyi seç
  useEffect(() => {
    api.get("/owners").then(r => {
      // Veritabanı dil farklılıklarına (owner_name / malik_adi) karşı zırhlı sıralama
      const sorted = (r.data || []).slice().sort((a, b) => 
        (a.owner_name || a.malik_adi || "").localeCompare(b.owner_name || b.malik_adi || "", "tr")
      );
      setOwners(sorted);
      
      // Eğer DebtorsReport'tan bir kod geldiyse ONU KULLAN, gelmediyse alfabetik ilk kişiyi kullan
      const targetCode = initialKodu || sorted[0]?.accounting_code || sorted[0]?.hesap_kodu || "120.01";
      setSelected(targetCode);
      loadStatement(targetCode);
    });
  }, []);

  // 3. DIŞARIDAN DEĞİŞİM: Raporlar menüsündeyken peş peşe farklı kişilere tıklanırsa yakala
  useEffect(() => {
    if (initialKodu && owners.length > 0) {
      setSelected(initialKodu);
      loadStatement(initialKodu, startDate);
    }
  }, [initialKodu]);

  const choose = e => {
    setSelected(e.target.value);
    loadStatement(e.target.value, startDate);
  };

  const changeStart = e => {
    setStartDate(e.target.value);
    loadStatement(selected, e.target.value);
  };

  const site = data?.settings?.site_name || "VADİ EVLERİ";

  return (
    <>
      <PageHead 
        eyebrow="HESAP DÖKÜMÜ" 
        title="Bakiye raporu" 
        sub="Her malik için aylık aidat tahakkukları, ödemeler ve bakiye." 
        action={
          <button className="secondary" onClick={() => window.print()} data-testid="print-balance-button">
            <Printer size={16}/> Yazdır / PDF
          </button>
        }
      />
      
      <section className="panel balance-selector no-print">
        <div>
          <span className="eyebrow">MALİK HESABI SEÇİN</span>
          <select value={selected} onChange={choose} data-testid="balance-owner-select">
            {owners.map(x => {
              const code = x.accounting_code || x.hesap_kodu;
              const name = x.owner_name || x.malik_adi || "(Boş)";
              const no = x.villa_no || x.daire_kodu || "—";
              return (
                <option key={code} value={code}>
                  {name} · {no} · {code}
                </option>
              );
            })}
          </select>
        </div>
        <div className="balance-actions">
          <label>Başlangıç tarihi
            <input type="date" value={startDate} onChange={changeStart} data-testid="balance-start-date"/>
          </label>
        </div>
      </section>
      
      {data && (
        <>
          <div className="balance-summary no-print">
            <div>
              <small>Toplam aidat</small>
              <strong>{money(data.totals.aidat)}</strong>
            </div>
            <div>
              <small>Toplam ödeme</small>
              <strong className="positive">{money(data.totals.payment)}</strong>
            </div>
            <div>
              <small>Güncel bakiye</small>
              <strong className={data.totals.balance > 0 ? "negative" : "positive"}>{money(data.totals.balance)}</strong>
              <span>{data.totals.balance > 0 ? "Borçlu" : "Alacaklı / Kapalı"}</span>
            </div>
          </div>
          
          <section className="panel statement" id="statement-print">
            <div className="statement-top">
              <div>
                <span className="eyebrow">{site.toUpperCase()}</span>
                <h3>ADI SOYADI: {data.owner?.owner_name || data.owner?.malik_adi || "—"}</h3>
                <p>Hesap kodu: <b>{selected}</b> · Villa No: <b>{data.owner?.villa_no || data.owner?.daire_kodu}</b> · Rapor tarihi: <b>{fmtTR(data.report_date)}</b></p>
              </div>
              <div className="statement-brand">{site[0] || "V"}<span>{site.toUpperCase()}</span></div>
            </div>
            
            <div className="table-panel">
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>AY</th>
                    <th>YIL</th>
                    <th>AİDAT TARİHİ</th>
                    <th className="right">AİDAT TUTARI</th>
                    <th className="right">EK BORÇ</th>
                    <th>VADE TARİHİ</th>
                    <th className="right">ÖDEME TUTARI</th>
                    <th>ÖDEME TARİHİ</th>
                    <th className="right">KALAN AİDAT BORÇLU(+) / ALACAKLI(-)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.no}>
                      <td>{r.no}</td>
                      <td>{r.ay}</td>
                      <td>{r.yil}</td>
                      <td>{fmtTR(r.aidat_date)}</td>
                      <td className="right">{money2(r.aidat_amount)}</td>
                      <td className="right">
                        {r.extra_amount ? money2(r.extra_amount) : ""}
                        {r.extra_charges && r.extra_charges.length > 0 && (
                          <small title={r.extra_charges.map(c => c.description).join(" · ")}>
                            {r.extra_charges[0].description.slice(0, 22)}
                            {r.extra_charges[0].description.length > 22 ? "…" : ""}
                          </small>
                        )}
                      </td>
                      <td>{fmtTR(r.aidat_date)}</td>
                      <td className="right">{r.payment_amount ? money2(r.payment_amount) : ""}</td>
                      <td>{r.payment_date || ""}</td>
                      <td className={`right ${r.balance > 0 ? "negative" : r.balance < 0 ? "positive" : ""}`}>
                        {money2(r.balance)}
                      </td>
                    </tr>
                  ))}
                  <tr className="totals-row">
                    <td colSpan="4" className="right"><b>TOPLAM</b></td>
                    <td className="right"><b>{money2(data.totals.aidat)}</b></td>
                    <td className="right"><b>{money2(data.totals.extra || 0)}</b></td>
                    <td/>
                    <td className="right"><b>{money2(data.totals.payment)}</b></td>
                    <td/>
                    <td className={`right ${data.totals.balance > 0 ? "negative" : "positive"}`}>
                      <b>{money2(data.totals.balance)}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!data.rows.length && <div className="empty">Bu hesaba ait kayıt bulunmuyor.</div>}
          </section>
        </>
      )}
    </>
  );
}